import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Stack,
} from "@mui/material";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";
import { FONT_SIZE } from "../../../constants/fontSizes";

const ConsentConfirmModal = ({
  open,
  title,
  description,
  children,
  onConfirm,
  onLogout,
  isSubmitting = false,
}) => {
  const isDesktop = useIsDesktop();

  // 最下部までスクロールしたか
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const contentRef = useRef(null);

  // スクロール位置監視
  const handleScroll = () => {
    const el = contentRef.current;

    if (!el) return;

    const reachedBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 10;

    if (reachedBottom) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="consent-dialog-title"
      fullWidth
      maxWidth={isDesktop ? "md" : "sm"}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
      }}
      PaperProps={{
        sx: {
          borderRadius: isDesktop ? 3 : 2,
          mx: isDesktop ? 0 : 2,
          maxHeight: "90vh",
          display: "flex",
        },
      }}
    >
      <DialogContent
        sx={{
          p: isDesktop ? 4 : 2,
          pb: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* タイトル */}
        <Typography
          id="consent-dialog-title"
          variant={isDesktop ? "h5" : "h6"}
          fontWeight="bold"
          sx={{
            mb: isDesktop ? 2 : 1.5,
            lineHeight: 1.4,
            fontSize: isDesktop ? FONT_SIZE.XXL : FONT_SIZE.BASE,
          }}
        >
          {title}
        </Typography>

        {/* 説明文 */}
        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              whiteSpace: "pre-line",
              fontSize: isDesktop ? FONT_SIZE.SM : FONT_SIZE.XS,
              lineHeight: 1.8,
            }}
          >
            {description}
          </Typography>
        )}

        {/* 利用規約本文 */}
        {children && (
          <Box
            ref={contentRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: isDesktop ? 2 : 1.5,
              bgcolor: "#fff",
            }}
          >
            {children}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 4 },
          pt: 1,
          pb: 3,
          flexShrink: 0,
        }}
      >
        <Stack spacing={1} width="100%">
          {/* 同意ボタン */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={!hasScrolledToBottom || isSubmitting}
            onClick={onConfirm}
            sx={{
              borderRadius: 9999,
              px: 1,
              fontWeight: "bold",
              color: "#fff",
              minHeight: 48,
              fontSize: isDesktop ? FONT_SIZE.BASE : FONT_SIZE.SM,
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            {isSubmitting ? "処理中..." : "同意する"}
          </Button>

          {/* ログアウト */}
          <Button
            variant="outlined"
            color="primary"
            disabled={isSubmitting}
            onClick={onLogout}
            sx={{
              borderRadius: 9999,
              minHeight: 48,
              fontSize: isDesktop ? FONT_SIZE.BASE : FONT_SIZE.SM,
            }}
          >
            ログアウト
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentConfirmModal;
